import React, { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  fetchScheduledObservations,
  selectScheduledObservationsByContext,
  selectScheduledObservationsByIds,
} from "./slice"
import { useLocation, useNavigate } from "react-router-dom"
import { updateSearchParamsWithFilters } from "../filters/filters"
import Layout from "../../ui/Layout"
import NavMain from "../../app/NavMain"
import ScrollablePage from "../../ui/ScrollablePage"
import Paper from "../../ui/Paper"
import { Helmet } from "react-helmet-async"
import DataPageHeader from "../../ui/DataPageHeader"
import ScheduledObservationsTable from "./ScheduledObservationsTable"
import { parseScheduledObservationsFilters } from "./filters"
import ErrorMsg from "../../ui/ErrorMsg"

const PAGE_SIZE = 50 // Default initial and subsequent page size

/* ObservationsTable renders a given list of observations. */
const ScheduledObservationsList = () => {
  const dispatch = useDispatch()

  // we get any matching scheduled observations for this RSO
  const scheduledObservationsMatches = useSelector((state) =>
    selectScheduledObservationsByContext(state, "root")
  )
  // We get the full objects from the ids that match
  const scheduledObservations = useSelector((state) =>
    selectScheduledObservationsByIds(
      state,
      scheduledObservationsMatches?.ids || []
    )
  )

  // Parse parameters into filter object (which drives filters UI state)
  // Note: We are using the URL as the "state" here, and updates
  // perform a history replacement (so as not to mess up "back" button
  // behaviour too much - though we may want to apply a heuristic like
  // small changes replace history, big changes push history)
  const location = useLocation()
  const navigate = useNavigate()
  let filter = {}
  let urlParams = {}

  urlParams = new URLSearchParams(location.search)
  filter = parseScheduledObservationsFilters(urlParams)
  updateSearchParamsWithFilters(urlParams, filter)
  useEffect(() => {
    dispatch(
      fetchScheduledObservations({
        filter,
        page: { offset: 0, limit: PAGE_SIZE },
        context: ["root"],
      })
    )
  }, [urlParams.toString()])

  // How we apply a filter update. Filter state is entirely in the URL query parameters
  const setFilter = (filter = {}) => {
    const updUrlParams = updateSearchParamsWithFilters(urlParams, filter)
    navigate({
      pathname: location.pathname,
      search: `?${updUrlParams}`,
    })
  }

  // How many more observations are available to load?
  const loadableCount = Math.min(
    PAGE_SIZE,
    scheduledObservations &&
      scheduledObservations.length &&
      scheduledObservationsMatches &&
      scheduledObservationsMatches.page &&
      scheduledObservationsMatches.page.total_count
      ? scheduledObservationsMatches.page.total_count -
          scheduledObservations.length
      : 50
  )

  // Event handler for 'Show xxx more' button that was clicked
  const handleLoadMore = () => {
    const lastIndex = scheduledObservations && scheduledObservations.length

    const page = {
      offset: lastIndex,
      limit: loadableCount,
    }
    dispatch(fetchScheduledObservations({ filter, page, context: ["root"] }))
  }

  return (
    <Layout>
      <NavMain key="nav-left" />
      <ScrollablePage>
        <Paper>
          <Helmet>
            <title>Scheduled observations - Astrosite</title>
          </Helmet>

          <DataPageHeader
            title="Scheduled observations"
            subtitle={
              <>
                {scheduledObservations.length > 0 && (
                  <span>
                    Showing{" "}
                    <strong>
                      {scheduledObservationsMatches?.page?.end_index} of{" "}
                      <strong className="count total_count">
                        {scheduledObservationsMatches?.page?.total_count}
                      </strong>
                      <span> scheduled observations</span>
                    </strong>
                    {scheduledObservationsMatches?._loadStatus == "loading" && (
                      <span> updating...</span>
                    )}
                  </span>
                )}
                {scheduledObservations.length == 0 &&
                  scheduledObservationsMatches?._loadStatus == "loading" && (
                    <span>loading...</span>
                  )}
                {scheduledObservationsMatches?._loadStatus == "rejected" && (
                  <ErrorMsg
                    msg={
                      "There is a problem fetching the scheduled observations"
                    }
                    showIcon={true}
                  />
                )}
              </>
            }
          />
          <ScheduledObservationsTable
            scheduledObservations={scheduledObservations}
            filter={filter}
            setFilter={setFilter}
          />
          {scheduledObservations &&
            scheduledObservations.length == 0 &&
            scheduledObservations?._loadStatus == "idle" && (
              <p>There are no scheduled observations matching your criteria.</p>
            )}
          <footer className="actions">
            {scheduledObservations.length > 0 &&
              scheduledObservations.length !==
                scheduledObservationsMatches?.page?.total_count && (
                <>
                  <button
                    onClick={handleLoadMore}
                    disabled={
                      scheduledObservationsMatches?._loadStatus == "loading"
                    }
                  >
                    {scheduledObservationsMatches?._loadStatus == "loading"
                      ? "Loading..."
                      : `Show ${loadableCount} more`}
                  </button>
                </>
              )}
          </footer>
        </Paper>
      </ScrollablePage>
    </Layout>
  )
}

export default ScheduledObservationsList
